import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { ExpenseStatsPoint } from '../../../../types/dashboard';
import { formatCurrencyShort } from '../../../../../shared/utils';
import { ADMIN_STAT_CHART_MAX_BAR_SIZE } from '../../reportChartConfig';

type ExpenseClaimedReportGraphProps = {
    points: ExpenseStatsPoint[];
};

const ExpenseTooltip = ({
    active,
    payload,
    label,
}: TooltipProps<number, string>) => {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload as ExpenseStatsPoint;

    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
            }}
        >
            <Typography variant='caption'>{label}</Typography>
            <Typography variant='body2'>
                Claims: {point.expenseCount}
            </Typography>
            <Typography variant='body2'>
                Amount: {formatCurrencyShort(point.totalExpenseClaimed)}
            </Typography>
        </Box>
    );
};

export default function ExpenseClaimedReportGraph({
    points,
}: ExpenseClaimedReportGraphProps) {
    const theme = useTheme();

    if (!points?.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No expense claims found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%', height: 420, mt: 3 }}>
            <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                    data={points}
                    margin={{ left: 30 }}
                    maxBarSize={ADMIN_STAT_CHART_MAX_BAR_SIZE}
                >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='label' />
                    <YAxis />
                    <Tooltip content={<ExpenseTooltip />} />
                    <Legend />
                    <Bar
                        dataKey='expenseCount'
                        name='Claims'
                        fill={theme.palette.textAccent.main}
                    />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
